import classnames from "classnames";
import CopyButton from "@/common/components/HDCopyButton";
import CheckCircleIcon from "@/assets/hd-icons/check-circle.svg?react";
import XCircleIcon from "@/assets/hd-icons/x-circle.svg?react";

export type CallerIdVerificationVariant = "light" | "dark";
export type CallerIdVerificationStatus = "pending" | "verified" | "failed";

export interface CallerIdVerificationContentProps {
    variant: CallerIdVerificationVariant;
    status: CallerIdVerificationStatus;
    verificationCode: string;
    copyButtonId?: string;
}

/**
 * Shared verification UI: code display + copy, or success/failed message.
 * Light variant: modal/settings sizing. Dark variant: widget/dialer sizing.
 */
function CallerIdVerificationContent({
    variant,
    status,
    verificationCode,
    copyButtonId = "caller-id-verification-copy",
}: CallerIdVerificationContentProps) {
    const isLight = variant === "light";

    if (status === "pending") {
        // Code + copy block
        return (
            <div className="flex w-full items-stretch rounded-lg">
                <div
                    className={classnames(
                        "flex flex-1 rounded-lg items-center justify-center px-8 py-2",
                        isLight ? "bg-gray-100" : "bg-shadow-700",
                    )}
                >
                    <span
                        className={classnames(
                            "text-center tracking-widest text-3xl font-jakarta",
                            isLight ? "text-gray-600 font-medium" : "text-white font-semibold",
                        )}
                    >
                        {verificationCode}
                    </span>
                </div>
                <CopyButton
                    id={copyButtonId}
                    onCopy={() => {}}
                    copyValue={verificationCode}
                    tooltipText="Copy code"
                    className={classnames(
                        "flex items-center justify-center rounded-lg transition-colors px-5 ml-4",
                        isLight
                            ? "border-0 text-gray-600 bg-gray-100 hover:bg-gray-200"
                            : "text-gray-400 bg-shadow-700 hover:bg-shadow-600",
                    )}
                    iconClassName="w-5 h-5"
                />
            </div>
        );
    }

    if (status === "failed") {
        return (
            <div className="flex flex-col items-center gap-2">
                <div className="w-16 rounded-full flex items-center justify-center">
                    <XCircleIcon className="w-10 h-10 text-red-500" />
                </div>
                <h3
                    className={classnames(
                        "text-2xl text-center",
                        isLight ? "text-gray-600 font-medium" : "text-white font-semibold",
                    )}
                >
                    Not Verified
                </h3>
                <p className={classnames("text-center text-sm max-w-xs", isLight ? "text-gray-500" : "text-gray-400")}>
                    The code may have been entered incorrectly, or the call wasn't answered. You can try again.
                </p>
            </div>
        );
    }

    // status === "verified"
    return (
        <div className="flex flex-col items-center gap-2 w-full py-6 justify-center">
            <div className="w-16 rounded-full flex items-center justify-center">
                <CheckCircleIcon
                    className={classnames("w-10 h-10", isLight ? "text-max-900" : "text-max-500")}
                    aria-hidden
                />
            </div>
            <p
                className={classnames(
                    "text-center text-[2rem]",
                    isLight ? "text-gray-600 font-medium" : "text-white font-semibold",
                )}
            >
                Phone Verified
            </p>
        </div>
    );
}

export default CallerIdVerificationContent;
